import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { appName } from "../app.json";
import { useQuery } from "@apollo/react-hooks";
import { LIST, DUE_CAREPLAN, TERMINATION_CAREPLAN } from "../queries/note-dashboard.query";
import LoadingSpinner from "../partials/loading";
import auth from "../providers/Auth.Provider";
import { Redirect } from "react-router-dom";
import { Doughnut, Pie, Bar } from "react-chartjs-2";
import { toast } from "react-toastify";
import { CleanMessage, CleanNumber } from "../util/app";
import { EventEmitter } from "../event/EventEmitter";
import { EVENT_TARGET } from "../event/model";
import DueCarePlanList from "../partials/Dashboard/DueCareplanList";
import Pagination from "../partials/pagination";

function Dashboard({ location }) {
    const title = "Dashboard";

    const [items, setItems] = useState([]);
    const [genders, setGender] = useState([]);
    const [gl, setGl] = useState([]);
    const [duePage, setDuePage] = useState(1);
    const [tPage] = useState(1);
    const [limit] = useState(10);
    const [genderRatio, setGenderRatio] = useState(undefined);

    // data
    const { loading, data, error, refetch } = useQuery(LIST, {
        onCompleted: (data) => {
            const list = data.PatientAdmissionRatio.map((i) => i.count);
            const g = data.PatientGenderRatio.docs.map((i) => i.count);
            const labels = data.PatientGenderRatio.docs.map((i) => i.title);
            setItems(list);
            setGender(g);
            setGl(labels);
            let _data = { ...data.GetAdmissionStats };
            _data.datasets = _data.dataSets.map((item, idx) => ({
                ...item,
                fill: true,
                lineTension: 0.1,
                backgroundColor: `rgba(75,192,192,${0.4 + idx})`,
                borderColor: "rgba(75,192,192,1)",
                borderCapStyle: "butt",
                borderDash: [],
                borderDashOffset: 0.0,
                borderJoinStyle: "miter",
                pointBorderColor: "rgba(75,192,192,1)",
                pointBackgroundColor: "#fff",
                pointBorderWidth: 1,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: "rgba(75,192,192,1)",
                pointHoverBorderColor: "rgba(220,220,220,1)",
                pointHoverBorderWidth: 2,
                pointRadius: 1,
                pointHitRadius: 10,
            }));
            setGenderRatio(_data);
        },
        onError: (e) => toast.error(CleanMessage(e.message)),
    });

    const { loading: dueLoading, data: dueData, refetch: dueRetch, fetchMore: dueFetchMore } = useQuery(DUE_CAREPLAN, {
        notifyOnNetworkStatusChange: true,
        onError: (e) => toast.error(CleanMessage(e.message)),
        variables: { page: duePage, limit },
    });

    const { refetch: tRetch, fetchMore: tFetchMore } = useQuery(TERMINATION_CAREPLAN, {
        notifyOnNetworkStatusChange: true,
        onError: (e) => toast.error(CleanMessage(e.message)),
        variables: { page: duePage, limit },
    });

    useEffect(() => {
        tFetchMore({
            variables: { page: tPage, limit },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!prev) return prev;
                return {
                    GetTerminableCareplan: fetchMoreResult.GetTerminableCareplan,
                };
            },
        });
    }, [tPage, limit, tFetchMore]);

    useEffect(() => {
        dueFetchMore({
            variables: { page: duePage, limit },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!prev) return prev;
                return {
                    GetDueCareplan: fetchMoreResult.GetDueCareplan,
                };
            },
        });
    }, [duePage, limit, dueFetchMore]);

    if (error && auth.authProvider.IsAuthenticationError(error.message)) {
        auth.authProvider.LogOut();
        return (
            <Redirect
                to={{
                    pathname: "/login",
                    search: `?redirect=${location.pathname}`,
                    state: {
                        from: {
                            pathname: location.pathname,
                        },
                    },
                }}
            />
        );
    }

    EventEmitter.subscribe(EVENT_TARGET.PATIENT, () => {
        refetch();
    });
    EventEmitter.subscribe(EVENT_TARGET.CARE_PLAN, () => dueRetch() || tRetch());
    return (
        <>
            <Helmet>
                <title>
                    {title} | {appName}
                </title>
            </Helmet>
            <LoadingSpinner loading={loading} bar={true} />
            <div className="row equal">
                <div className="col-12 col-md-3">
                    {data && (
                        <div className="row fade-in justify-content-center">
                            <div className="col-12 p-2">
                                <div className="info-box bg-success">
                                    <span className="info-box-icon push-bottom bg-transprnt">
                                        <i className="material-icons">supervised_user_circle</i>
                                    </span>
                                    <div className="info-box-content m-b-10">
                                        <span className="info-box-text">Total Patient</span>
                                        <span className="info-box-number">{CleanNumber(data.CountPatient)}</span>
                                        <span className="progress-description">Total Patient Admitted: {CleanNumber(data.CountPatient - data.NoOfOutPatient)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 p-2">
                                <div className="info-box bg-blue">
                                    <span className="info-box-icon push-bottom bg-transprnt">
                                        <i className="material-icons">person_add</i>
                                    </span>
                                    <div className="info-box-content m-b-10">
                                        <span className="info-box-text">New Patient</span>
                                        <span className="info-box-number">{CleanNumber(data.NoOfnewPatient)}</span>
                                        <span className="progress-description">Total Patient Created Within 24Hrs</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 p-2">
                                <div className="info-box bg-orange">
                                    <span className="info-box-icon push-bottom bg-transprnt">
                                        <i className="material-icons">directions_walk</i>
                                    </span>
                                    <div className="info-box-content m-b-10">
                                        <span className="info-box-text">Patient On Leave</span>
                                        <span className="info-box-number">{CleanNumber(data.NoOfPatientOnLeave)}</span>
                                        <span className="progress-description">Total Patient on Leave</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 p-2">
                                <div className="info-box bg-pink">
                                    <span className="info-box-icon push-bottom bg-transprnt">
                                        <i className="material-icons">people_outline</i>
                                    </span>
                                    <div className="info-box-content m-b-10">
                                        <span id="tt3" className="info-box-text">
                                            Avg Patient to Doctor
                                        </span>
                                        <span className="info-box-number">{data.PatientDocAvg.doc.avg}</span>
                                        <span className="progress-description">Total Doctor: {data.PatientDocAvg.doc.total}</span>
                                        <div className="mdl-tooltip" data-mdl-for="tt3">
                                            Avg Patient to Doctor
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="col-12 col-md-9">
                    <div className="row justify-content-center">
                        {data && (
                            <>
                                <div className="col-md-6 col-12 p-2">
                                    <div className="card">
                                        <div className="card-head">
                                            <header>Patient Ratio</header>
                                        </div>
                                        <div className="card-body">
                                            <Pie
                                                data={{
                                                    datasets: [
                                                        {
                                                            data: items,
                                                            backgroundColor: ["#2ed8b6", "orange"],
                                                        },
                                                    ],
                                                    labels: ["InPatient", "OutPatient"],
                                                }}
                                                options={{
                                                    animation: {
                                                        animateScale: true,
                                                    },
                                                    maintainAspectRatio: true,
                                                    cutoutPercentage: 0,
                                                    responsive: true,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 p-2">
                                    <div className="card">
                                        <div className="card-head">
                                            <header>Patient Gender Ratio</header>
                                        </div>
                                        <div className="card-body">
                                            <Doughnut
                                                data={{
                                                    datasets: [
                                                        {
                                                            data: genders,
                                                            backgroundColor: ["#ef476f", "#ffd166", "#073b4c"],
                                                        },
                                                    ],
                                                    labels: gl,
                                                }}
                                                options={{
                                                    animation: {
                                                        animateScale: true,
                                                    },
                                                    maintainAspectRatio: true,
                                                    cutoutPercentage: 50,
                                                    responsive: true,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="col-12 col-md-12">
                            <div className="card h-100 min-card">
                                <div className="card-head">
                                    <div className="tools">
                                        <a className="t-collapse btn-color fa fa-chevron-down" href="javascript:;"></a>
                                    </div>
                                    <header>Careplan due for Review</header>
                                </div>
                                <div className="card-body">
                                    <LoadingSpinner loading={dueLoading} />
                                    {dueData && <DueCarePlanList items={dueData.GetDueCareplan.docs} />}
                                </div>
                                <div className="card-footer">
                                    {dueData && (
                                        <Pagination
                                            {...dueData.GetDueCareplan}
                                            length={dueData.GetDueCareplan.docs.length}
                                            onPageClicked={async (page) => {
                                                setDuePage(page);
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <div className="row equal">
                                <div className="col-12 col-md-12 p-2">
                                    <div className="card">
                                        <div className="card-head">
                                            <header>Gender Chart</header>
                                        </div>
                                        <div className="card-body" style={{ minHeight: "300px" }}>
                                            <Bar
                                                data={genderRatio}
                                                height={70}
                                                options={{
                                                    maintainAspectRatio: false,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Dashboard;
